import clsx from 'clsx';
import { Text } from './Text';
import { ColumnDef, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

interface TableProps<TData, TValue> {
  className?: string;
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
}

export function Table<TData, TValue>({ className, columns, data }: TableProps<TData, TValue>) {
  const { t } = useTranslation();
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="relative w-full overflow-x-auto">
      <table className={clsx('w-full', className)}>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th key={header.id} className="px-md bg-neutral-subtle h-[52px] text-left">
                  <Text variant="label" strong>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </Text>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.length ? (
            table.getRowModel().rows.map((row) => (
              <tr key={row.id} className="odd:bg-base-white even:bg-neutral-soft-surface">
                {row.getVisibleCells().map((cell) => {
                  const cellValue = cell.getValue();
                  const cellContent = flexRender(cell.column.columnDef.cell, cell.getContext());
                  const isPrimitive =
                    typeof cellValue === 'string' || typeof cellValue === 'number';

                  return (
                    <td key={cell.id} className="px-md h-[52px] text-left">
                      {isPrimitive ? (
                        <Text variant="label" size="md">
                          {cellValue}
                        </Text>
                      ) : (
                        cellContent
                      )}
                    </td>
                  );
                })}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={columns.length} className="p-lg bg-neutral-soft-surface text-center">
                <Text strong>{t('common.tableEmpty')}</Text>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
